import { barcelonaOfflinePayload, barcelonaRegistrySummary, offlineCityPacks } from './data/offlinePacks';

export interface BarcelonaMapManifest {
  schemaVersion: number;
  id: string;
  city: string;
  dataset: string;
  source: {
    provider: string;
    build: string;
    url: string;
    datasetTimestamp: string;
    dataLicense: string;
    attribution: string;
  };
  extraction: {
    tool: string;
    requestedBounds: [number, number, number, number];
    archiveBounds: [number, number, number, number];
    minZoom: number;
    maxZoom: number;
    pmtilesSpecVersion: number;
    tileType: string;
  };
  basemap: {
    url: string;
    bytes: number;
    sha256: string;
  };
  glyphs: {
    template: string;
    fontstack: string;
    range: string;
    bytes: number;
    sourceCommit: string;
    sourceBlobSha: string;
  };
}

export type MapPackManifest = BarcelonaMapManifest;
export interface InstalledMapPack { file: File; manifest: MapPackManifest; }

export interface InstalledBarcelonaBasemap {
  file: File;
  manifest: BarcelonaMapManifest;
}

export interface OfflineDiagnostics {
  online: boolean;
  standalone: boolean;
  serviceWorkerSupported: boolean;
  serviceWorkerControlled: boolean;
  cacheName: string | null;
  cachedEntries: number;
  shellCached: boolean;
  walletCached: boolean;
  persistentStorage: boolean | null;
  opfsSupported: boolean;
  opfsError: string | null;
  usageBytes: number | null;
  quotaBytes: number | null;
  barcelonaCatalogInstalled: boolean;
  barcelonaMapInstalled: boolean;
  barcelonaMapBytes: number | null;
  barcelonaMapSha256: string | null;
}

type SafariNavigator = Navigator & { standalone?: boolean };
type StorageManagerWithDirectory = StorageManager & {
  getDirectory?: () => Promise<FileSystemDirectoryHandle>;
};

type MapRuntimeManifest = {
  schemaVersion: number;
  purpose: string;
  urls: string[];
};

export function isStandalone(): boolean {
  return window.matchMedia('(display-mode: standalone)').matches || Boolean((navigator as SafariNavigator).standalone);
}

async function currentTravelCache(): Promise<{ name: string | null; entries: number }> {
  if (!('caches' in window)) return { name: null, entries: 0 };
  const names = (await caches.keys()).filter((name) => name.startsWith('europa-2026-')).sort();
  const name = names.at(-1) ?? null;
  if (!name) return { name: null, entries: 0 };
  const cache = await caches.open(name);
  return { name, entries: (await cache.keys()).length };
}

async function cached(path: string): Promise<boolean> {
  if (!('caches' in window)) return false;
  return Boolean(await caches.match(path));
}

async function cityDirectory(create = false): Promise<FileSystemDirectoryHandle> {
  const storage = navigator.storage as StorageManagerWithDirectory | undefined;
  if (!storage?.getDirectory) throw new Error('OPFS unavailable');
  const root = await storage.getDirectory();
  const app = await root.getDirectoryHandle('europa-2026', { create });
  const maps = await app.getDirectoryHandle('maps', { create });
  return maps.getDirectoryHandle('barcelona', { create });
}


const STORAGE_UNAVAILABLE = 'Este navegador no permite guardar el mapa en esta sesión. Abre una pestaña normal (fuera del modo privado) o la WebApp instalada y vuelve a descargar el mapa. El mapa con conexión sigue disponible.';

export async function checkMapStorage(): Promise<{ writable: boolean; error: string | null }> {
  let city: FileSystemDirectoryHandle | undefined;
  let writable: FileSystemWritableFileStream | undefined;
  const name = `.storage-check-${crypto.randomUUID()}`;
  try {
    city = await cityDirectory(true);
    const handle = await city.getFileHandle(name, { create: true });
    writable = await handle.createWritable();
    await writable.write('ok');
    await writable.close();
    writable = undefined;
    if (await (await handle.getFile()).text() !== 'ok') throw new Error('Storage readback failed');
    return { writable: true, error: null };
  } catch {
    return { writable: false, error: STORAGE_UNAVAILABLE };
  } finally {
    try { await writable?.abort(); } catch { /* Already closed or unavailable. */ }
    try { await city?.removeEntry(name); } catch { /* Probe could not be created. */ }
  }
}

export async function requireMapStorage(): Promise<void> {
  const capability = await checkMapStorage();
  if (!capability.writable) throw new Error(capability.error ?? STORAGE_UNAVAILABLE);
}

async function hasBarcelonaCatalog(): Promise<boolean> {
  try {
    const city = await cityDirectory();
    const file = await city.getFileHandle('catalog.json');
    const persisted = await (await file.getFile()).text();
    const digest = await sha256Text(persisted);
    return digest === barcelonaRegistrySummary.catalogSha256;
  } catch {
    return false;
  }
}

function isMapManifest(value: unknown): value is BarcelonaMapManifest {
  if (!value || typeof value !== 'object') return false;
  const manifest = value as Partial<BarcelonaMapManifest>;
  return manifest.schemaVersion === 1
    && typeof manifest.id === 'string'
    && typeof manifest.city === 'string'
    && typeof manifest.basemap?.url === 'string'
    && Number.isFinite(manifest.basemap?.bytes)
    && typeof manifest.basemap?.sha256 === 'string'
    && /^[0-9a-f]{64}$/.test(manifest.basemap.sha256)
    && Array.isArray(manifest.extraction?.archiveBounds)
    && manifest.extraction?.tileType === 'MVT';
}

export async function fetchBarcelonaMapManifest(): Promise<BarcelonaMapManifest> {
  const response = await fetch('/maps/barcelona/pack.json', { cache: 'no-store' });
  if (!response.ok) throw new Error(`Barcelona pack metadata unavailable (${response.status})`);
  const value: unknown = await response.json();
  if (!isMapManifest(value)) throw new Error('Barcelona pack metadata is invalid');

  if (barcelonaRegistrySummary.basemapSha256 && value.basemap.sha256 !== barcelonaRegistrySummary.basemapSha256) {
    throw new Error('Barcelona basemap SHA-256 differs from the release registry');
  }
  if (barcelonaRegistrySummary.basemapBytes && value.basemap.bytes !== barcelonaRegistrySummary.basemapBytes) {
    throw new Error('Barcelona basemap byte count differs from the release registry');
  }
  if (barcelonaRegistrySummary.basemapSourceBuild && value.source.build !== barcelonaRegistrySummary.basemapSourceBuild) {
    throw new Error('Barcelona basemap source build differs from the release registry');
  }
  return value;
}

async function readInstalledBarcelonaBasemap(): Promise<InstalledBarcelonaBasemap | null> {
  try {
    const city = await cityDirectory();
    const metadataFile = await city.getFileHandle('pack.json');
    const metadataText = await (await metadataFile.getFile()).text();
    const value: unknown = JSON.parse(metadataText);
    if (!isMapManifest(value)) return null;
    if (barcelonaRegistrySummary.basemapSha256 && value.basemap.sha256 !== barcelonaRegistrySummary.basemapSha256) return null;
    const file = await (await city.getFileHandle('basemap.pmtiles')).getFile();
    if (file.size !== value.basemap.bytes) return null;
    return { file, manifest: value };
  } catch {
    return null;
  }
}

export async function getInstalledBarcelonaBasemap(): Promise<InstalledBarcelonaBasemap | null> {
  return readInstalledBarcelonaBasemap();
}

export async function getOfflineDiagnostics(): Promise<OfflineDiagnostics> {
  const storage = navigator.storage as StorageManagerWithDirectory | undefined;
  const estimatePromise: Promise<StorageEstimate> = storage?.estimate
    ? storage.estimate().catch(() => ({} as StorageEstimate))
    : Promise.resolve({} as StorageEstimate);

  const [{ name, entries }, shellCached, walletCached, estimate, persistentStorage, barcelonaCatalogInstalled, installedMap, mapStorage] = await Promise.all([
    currentTravelCache(),
    cached('/index.html'),
    cached('/wallet-pack.enc.json'),
    estimatePromise,
    storage?.persisted?.().catch(() => null) ?? Promise.resolve(null),
    hasBarcelonaCatalog(),
    readInstalledBarcelonaBasemap(),
    checkMapStorage(),
  ]);

  return {
    online: navigator.onLine,
    standalone: isStandalone(),
    serviceWorkerSupported: 'serviceWorker' in navigator,
    serviceWorkerControlled: Boolean(navigator.serviceWorker?.controller),
    cacheName: name,
    cachedEntries: entries,
    shellCached,
    walletCached,
    persistentStorage,
    opfsSupported: mapStorage.writable,
    opfsError: mapStorage.error,
    usageBytes: typeof estimate.usage === 'number' ? estimate.usage : null,
    quotaBytes: typeof estimate.quota === 'number' ? estimate.quota : null,
    barcelonaCatalogInstalled,
    barcelonaMapInstalled: Boolean(installedMap),
    barcelonaMapBytes: installedMap?.file.size ?? null,
    barcelonaMapSha256: installedMap?.manifest.basemap.sha256 ?? null,
  };
}

async function sha256Text(value: string): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(value));
  return [...new Uint8Array(digest)].map((byte) => byte.toString(16).padStart(2, '0')).join('');
}

const SHA256_CONSTANTS = new Uint32Array([0x428a2f98,0x71374491,0xb5c0fbcf,0xe9b5dba5,0x3956c25b,0x59f111f1,0x923f82a4,0xab1c5ed5,0xd807aa98,0x12835b01,0x243185be,0x550c7dc3,0x72be5d74,0x80deb1fe,0x9bdc06a7,0xc19bf174,0xe49b69c1,0xefbe4786,0x0fc19dc6,0x240ca1cc,0x2de92c6f,0x4a7484aa,0x5cb0a9dc,0x76f988da,0x983e5152,0xa831c66d,0xb00327c8,0xbf597fc7,0xc6e00bf3,0xd5a79147,0x06ca6351,0x14292967,0x27b70a85,0x2e1b2138,0x4d2c6dfc,0x53380d13,0x650a7354,0x766a0abb,0x81c2c92e,0x92722c85,0xa2bfe8a1,0xa81a664b,0xc24b8b70,0xc76c51a3,0xd192e819,0xd6990624,0xf40e3585,0x106aa070,0x19a4c116,0x1e376c08,0x2748774c,0x34b0bcb5,0x391c0cb3,0x4ed8aa4a,0x5b9cca4f,0x682e6ff3,0x748f82ee,0x78a5636f,0x84c87814,0x8cc70208,0x90befffa,0xa4506ceb,0xbef9a3f7,0xc67178f2]);

class IncrementalSha256 {
  private readonly state = new Uint32Array([
    0x6a09e667, 0xbb67ae85, 0x3c6ef372, 0xa54ff53a,
    0x510e527f, 0x9b05688c, 0x1f83d9ab, 0x5be0cd19,
  ]);
  private readonly buffer = new Uint8Array(64);
  private readonly words = new Uint32Array(64);
  private buffered = 0;
  private bytes = 0;

  update(input: Uint8Array): void {
    this.bytes += input.byteLength;
    let offset = 0;
    if (this.buffered) {
      const take = Math.min(64 - this.buffered, input.byteLength);
      this.buffer.set(input.subarray(0, take), this.buffered);
      this.buffered += take;
      offset = take;
      if (this.buffered === 64) { this.block(this.buffer); this.buffered = 0; }
    }
    while (offset + 64 <= input.byteLength) { this.block(input.subarray(offset, offset + 64)); offset += 64; }
    if (offset < input.byteLength) { this.buffer.set(input.subarray(offset)); this.buffered = input.byteLength - offset; }
  }

  private block(chunk: Uint8Array): void {
    const words = this.words;
    for (let i = 0; i < 16; i++) words[i] = ((chunk[i * 4] << 24) | (chunk[i * 4 + 1] << 16) | (chunk[i * 4 + 2] << 8) | chunk[i * 4 + 3]) >>> 0;
    for (let i = 16; i < 64; i++) {
      const a = words[i - 15], b = words[i - 2];
      const s0 = ((a >>> 7) | (a << 25)) ^ ((a >>> 18) | (a << 14)) ^ (a >>> 3);
      const s1 = ((b >>> 17) | (b << 15)) ^ ((b >>> 19) | (b << 13)) ^ (b >>> 10);
      words[i] = (words[i - 16] + s0 + words[i - 7] + s1) >>> 0;
    }
    let [a,b,c,d,e,f,g,h] = this.state;
    for (let i = 0; i < 64; i++) {
      const s1 = ((e >>> 6) | (e << 26)) ^ ((e >>> 11) | (e << 21)) ^ ((e >>> 25) | (e << 7));
      const choose = (e & f) ^ (~e & g);
      const t1 = (h + s1 + choose + SHA256_CONSTANTS[i] + words[i]) >>> 0;
      const s0 = ((a >>> 2) | (a << 30)) ^ ((a >>> 13) | (a << 19)) ^ ((a >>> 22) | (a << 10));
      const majority = (a & b) ^ (a & c) ^ (b & c);
      h = g; g = f; f = e; e = (d + t1) >>> 0; d = c; c = b; b = a; a = (t1 + s0 + majority) >>> 0;
    }
    this.state[0] = (this.state[0] + a) >>> 0; this.state[1] = (this.state[1] + b) >>> 0;
    this.state[2] = (this.state[2] + c) >>> 0; this.state[3] = (this.state[3] + d) >>> 0;
    this.state[4] = (this.state[4] + e) >>> 0; this.state[5] = (this.state[5] + f) >>> 0;
    this.state[6] = (this.state[6] + g) >>> 0; this.state[7] = (this.state[7] + h) >>> 0;
  }

  finish(): string {
    const totalBits = this.bytes * 8;
    const tail = new Uint8Array(this.buffered < 56 ? 64 : 128);
    tail.set(this.buffer.subarray(0, this.buffered)); tail[this.buffered] = 0x80;
    const view = new DataView(tail.buffer); view.setUint32(tail.length - 8, Math.floor(totalBits / 0x100000000)); view.setUint32(tail.length - 4, totalBits >>> 0);
    this.block(tail.subarray(0, 64)); if (tail.length === 128) this.block(tail.subarray(64));
    return [...this.state].map((word) => word.toString(16).padStart(8, '0')).join('');
  }
}

async function sha256File(file: File): Promise<string> {
  const hasher = new IncrementalSha256();
  const chunkBytes = 1024 * 1024;
  for (let offset = 0; offset < file.size; offset += chunkBytes) {
    hasher.update(new Uint8Array(await file.slice(offset, Math.min(offset + chunkBytes, file.size)).arrayBuffer()));
    await new Promise<void>((resolve) => setTimeout(resolve, 0));
  }
  return hasher.finish();
}

export async function installBarcelonaCatalog(): Promise<{ bytes: number; sha256: string }> {
  const serialized = JSON.stringify(barcelonaOfflinePayload);
  const sha256 = await sha256Text(serialized);
  if (sha256 !== barcelonaRegistrySummary.catalogSha256) throw new Error('Catalog checksum mismatch');

  const city = await cityDirectory(true);
  const file = await city.getFileHandle('catalog.json', { create: true });
  const writable = await file.createWritable();
  await writable.write(serialized);
  await writable.close();

  const persisted = await (await file.getFile()).text();
  if (await sha256Text(persisted) !== sha256) throw new Error('Persisted catalog checksum mismatch');
  return { bytes: new Blob([persisted]).size, sha256 };
}

async function cacheOfflineMapRuntimeAssets(): Promise<number> {
  if (!('caches' in window)) throw new Error('Cache Storage unavailable');
  const manifestResponse = await fetch('/offline-map-assets.json', { cache: 'no-store' });
  if (!manifestResponse.ok) throw new Error('Offline map runtime manifest unavailable');
  const runtime: MapRuntimeManifest = await manifestResponse.clone().json();
  if (runtime.schemaVersion !== 1 || !Array.isArray(runtime.urls) || runtime.urls.length === 0) {
    throw new Error('Offline map runtime manifest is invalid');
  }
  if (runtime.urls.some((url) => typeof url !== 'string' || !url.startsWith('/') || url.includes('basemap.pmtiles'))) {
    throw new Error('Offline map runtime manifest contains an unsafe URL');
  }

  const { name } = await currentTravelCache();
  if (!name) throw new Error('Application cache is not ready');
  const cache = await caches.open(name);
  await cache.put('/offline-map-assets.json', manifestResponse);

  for (const url of runtime.urls) {
    const response = await fetch(url, { cache: 'no-store' });
    if (!response.ok) throw new Error(`Unable to cache offline map runtime asset: ${url}`);
    await cache.put(url, response);
  }
  return runtime.urls.length;
}

export async function installBarcelonaMapPack(
  onProgress?: (receivedBytes: number, totalBytes: number) => void,
): Promise<{ bytes: number; sha256: string; runtimeAssets: number; manifest: BarcelonaMapManifest }> {
  const storage = navigator.storage as StorageManagerWithDirectory | undefined;
  if (!storage?.getDirectory) throw new Error('OPFS unavailable');

  await requireMapStorage();
  const existing = await readInstalledBarcelonaBasemap();
  if (existing && await sha256File(existing.file) === existing.manifest.basemap.sha256) {
    const runtimeAssets = await cacheOfflineMapRuntimeAssets();
    return { bytes: existing.file.size, sha256: existing.manifest.basemap.sha256, runtimeAssets, manifest: existing.manifest };
  }
  const manifest = await fetchBarcelonaMapManifest();
  const estimate = storage.estimate ? await storage.estimate().catch(() => ({} as StorageEstimate)) : {} as StorageEstimate;
  if (typeof estimate.quota === 'number' && typeof estimate.usage === 'number') {
    const available = Math.max(0, estimate.quota - estimate.usage);
    const required = Math.ceil(manifest.basemap.bytes * 1.5) + 20 * 1024 * 1024;
    if (available < required) throw new Error('Insufficient storage for Barcelona offline map');
  }

  const city = await cityDirectory(true);
  let wroteBasemap = false;
  try {
    const response = await fetch(manifest.basemap.url, { cache: 'no-store' });
    if (!response.ok) throw new Error(`Barcelona basemap download failed (${response.status})`);
    const contentLength = Number(response.headers.get('content-length'));
    if (Number.isFinite(contentLength) && contentLength > 0 && contentLength !== manifest.basemap.bytes) {
      throw new Error('Barcelona basemap Content-Length mismatch');
    }

    const handle = await city.getFileHandle('basemap.pmtiles', { create: true });
    wroteBasemap = true;
    const writable = await handle.createWritable();
    let received = 0;
    try {
      if (response.body) {
        const reader = response.body.getReader();
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          if (!value) continue;
          received += value.byteLength;
          if (received > manifest.basemap.bytes) throw new Error('Barcelona basemap exceeded expected size');
          await writable.write(value);
          onProgress?.(received, manifest.basemap.bytes);
        }
      } else {
        const buffer = await response.arrayBuffer();
        received = buffer.byteLength;
        await writable.write(buffer);
        onProgress?.(received, manifest.basemap.bytes);
      }
      await writable.close();
      wroteBasemap = true;
    } catch (error) {
      try { await writable.abort(); } catch { /* best effort */ }
      throw error;
    }

    if (received !== manifest.basemap.bytes) throw new Error('Barcelona basemap byte count mismatch');
    const persisted = await handle.getFile();
    if (persisted.size !== manifest.basemap.bytes) throw new Error('Persisted Barcelona basemap size mismatch');
    const sha256 = await sha256File(persisted);
    if (sha256 !== manifest.basemap.sha256) throw new Error('Persisted Barcelona basemap SHA-256 mismatch');

    const metadataHandle = await city.getFileHandle('pack.json', { create: true });
    const metadataWritable = await metadataHandle.createWritable();
    await metadataWritable.write(`${JSON.stringify(manifest, null, 2)}\n`);
    await metadataWritable.close();

    const runtimeAssets = await cacheOfflineMapRuntimeAssets();
    return { bytes: persisted.size, sha256, runtimeAssets, manifest };
  } catch (error) {
    if (wroteBasemap) {
      try { await city.removeEntry('basemap.pmtiles'); } catch { /* best effort */ }
    }
    try { await city.removeEntry('pack.json'); } catch { /* best effort */ }
    throw error;
  }
}

async function prepareOpfs(): Promise<void> {
  const storage = navigator.storage as StorageManagerWithDirectory | undefined;
  if (!storage?.getDirectory) return;
  const root = await storage.getDirectory();
  const app = await root.getDirectoryHandle('europa-2026', { create: true });
  await app.getDirectoryHandle('maps', { create: true });
  await app.getDirectoryHandle('receipts', { create: true });
}

async function refreshEncryptedWallet(): Promise<void> {
  try {
    await fetch('/wallet-pack.enc.json', { cache: 'no-store' });
  } catch {
    // Existing encrypted package remains usable when already cached.
  }
}

export async function prepareOfflineDevice(): Promise<OfflineDiagnostics> {
  const storage = navigator.storage as StorageManagerWithDirectory | undefined;

  if (storage?.persist) {
    try { await storage.persist(); } catch { /* Browser may decline persistence. */ }
  }

  try { await prepareOpfs(); } catch { /* Diagnostics expose OPFS support below. */ }

  if ('serviceWorker' in navigator) {
    try {
      const registration = await navigator.serviceWorker.ready;
      registration.active?.postMessage({ type: 'PRECACHE_NOW' });
    } catch {
      // Diagnostics below will expose the missing controller/cache state.
    }
  }

  await refreshEncryptedWallet();
  await new Promise((resolve) => setTimeout(resolve, 900));
  return getOfflineDiagnostics();
}

export function formatBytes(value: number | null): string {
  if (value == null) return 'No disponible';
  if (value < 1024) return `${value} B`;
  if (value < 1024 ** 2) return `${(value / 1024).toFixed(1)} KB`;
  if (value < 1024 ** 3) return `${(value / 1024 ** 2).toFixed(1)} MB`;
  return `${(value / 1024 ** 3).toFixed(2)} GB`;
}


function mapPack(packId: string) {
  const pack = offlineCityPacks.find((item) => item.id === packId);
  if (!pack?.basemapUrl || !pack.basemapSha256 || !pack.basemapBytes) throw new Error(`El paquete de ${packId} no está certificado.`);
  return { ...pack, basemapUrl: pack.basemapUrl, basemapSha256: pack.basemapSha256, basemapBytes: pack.basemapBytes };
}
async function cityDirectoryFor(packId: string, create = false): Promise<FileSystemDirectoryHandle> {
  const storage = navigator.storage as StorageManagerWithDirectory | undefined;
  if (!storage?.getDirectory) throw new Error('OPFS unavailable');
  const root = await storage.getDirectory();
  const app = await root.getDirectoryHandle('europa-2026', { create });
  const maps = await app.getDirectoryHandle('maps', { create });
  return maps.getDirectoryHandle(packId, { create });
}
function isGenericMapManifest(value: unknown, packId: string): value is MapPackManifest {
  if (!isMapManifest(value)) return false;
  const manifest = value as MapPackManifest;
  return manifest.id.startsWith(packId + '-v') && typeof manifest.city === 'string';
}
export async function fetchMapPackManifest(packId: string): Promise<MapPackManifest> {
  const pack = mapPack(packId);
  const manifestUrl = pack.basemapUrl;
  const response = await fetch(manifestUrl.replace(/basemap\.pmtiles$/, 'pack.json'), { cache: 'no-store' });
  if (!response.ok) throw new Error(`Metadatos de ${pack.city} no disponibles (${response.status})`);
  const value: unknown = await response.json();
  if (!isGenericMapManifest(value, packId)) throw new Error(`Metadatos de ${pack.city} no válidos`);
  if (value.basemap.url !== pack.basemapUrl || value.source?.build !== pack.basemapSourceBuild || value.basemap.sha256 !== pack.basemapSha256 || value.basemap.bytes !== pack.basemapBytes) throw new Error(`Integridad de ${pack.city} no coincide con el registro`);
  return value;
}
export async function getInstalledMapPack(packId: string): Promise<InstalledMapPack | null> {
  try {
    const pack = mapPack(packId), city = await cityDirectoryFor(packId);
    const value: unknown = JSON.parse(await (await (await city.getFileHandle('pack.json')).getFile()).text());
    if (!isGenericMapManifest(value, packId) || value.basemap.url !== pack.basemapUrl || value.source?.build !== pack.basemapSourceBuild || value.basemap.bytes !== pack.basemapBytes || value.basemap.sha256 !== pack.basemapSha256) return null;
    const file = await (await city.getFileHandle('basemap.pmtiles')).getFile();
    if (file.size !== pack.basemapBytes) return null;
    return { file, manifest: value };
  } catch { return null; }
}
export async function installMapPack(packId: string, onProgress?: (received: number, total: number) => void): Promise<{ bytes: number; sha256: string; manifest: MapPackManifest }> {
  const storage = navigator.storage as StorageManagerWithDirectory | undefined;
  if (!storage?.getDirectory) throw new Error('OPFS unavailable');
  await requireMapStorage();
  const existing = await getInstalledMapPack(packId);
  if (existing && await sha256File(existing.file) === existing.manifest.basemap.sha256) {
    await cacheOfflineMapRuntimeAssets();
    return { bytes: existing.file.size, sha256: existing.manifest.basemap.sha256, manifest: existing.manifest };
  }
  const pack = mapPack(packId), manifest = await fetchMapPackManifest(packId);
  const estimate = storage.estimate ? await storage.estimate().catch(() => ({} as StorageEstimate)) : {} as StorageEstimate;
  if (typeof estimate.quota === 'number' && typeof estimate.usage === 'number' && estimate.quota - estimate.usage < Math.ceil(manifest.basemap.bytes * 1.5) + 20 * 1024 * 1024) throw new Error('Espacio insuficiente para este mapa offline');
  const city = await cityDirectoryFor(packId, true);
  try {
    const response = await fetch(manifest.basemap.url, { cache: 'no-store' });
    if (!response.ok) throw new Error(`Descarga de ${pack.city} falló (${response.status})`);
    const declared = Number(response.headers.get('content-length'));
    if (Number.isFinite(declared) && declared > 0 && declared !== manifest.basemap.bytes) throw new Error('Content-Length no coincide');
    const handle = await city.getFileHandle('basemap.pmtiles', { create: true }), writable = await handle.createWritable();
    let received = 0;
    try {
      if (response.body) { const reader = response.body.getReader(); while (true) { const { done, value } = await reader.read(); if (done) break; if (!value) continue; received += value.byteLength; if (received > manifest.basemap.bytes) throw new Error('El archivo excede el tamaño certificado'); await writable.write(value); onProgress?.(received, manifest.basemap.bytes); } }
      else { const buffer = await response.arrayBuffer(); received = buffer.byteLength; await writable.write(buffer); onProgress?.(received, manifest.basemap.bytes); }
      await writable.close();
    } catch (error) { try { await writable.abort(); } catch {} throw error; }
    const file = await handle.getFile();
    if (received !== manifest.basemap.bytes || file.size !== manifest.basemap.bytes || await sha256File(file) !== manifest.basemap.sha256) throw new Error('La verificación SHA-256 del mapa falló');
    const metadata = await city.getFileHandle('pack.json', { create: true }), metadataWriter = await metadata.createWritable();
    await metadataWriter.write(JSON.stringify(manifest, null, 2) + '\n'); await metadataWriter.close();
    await cacheOfflineMapRuntimeAssets();
    return { bytes: file.size, sha256: manifest.basemap.sha256, manifest };
  } catch (error) { try { await city.removeEntry('basemap.pmtiles'); } catch {} try { await city.removeEntry('pack.json'); } catch {} throw error; }
}
